import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * ZoomIn component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconZoomIn extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M2.60547 10.3152C2.60547 6.05751 6.05702 2.60596 10.3147 2.60596C14.5724 2.60596 18.024 6.05751 18.024 10.3152C18.024 12.4118 17.1871 14.3129 15.8291 15.7026C15.8059 15.7209 15.7836 15.7408 15.7622 15.7622C15.7407 15.7836 15.7209 15.806 15.7026 15.8292C14.3128 17.1874 12.4115 18.0245 10.3147 18.0245C6.05702 18.0245 2.60547 14.5729 2.60547 10.3152ZM16.2751 17.3358C14.6688 18.7009 12.5879 19.5245 10.3147 19.5245C5.2286 19.5245 1.10547 15.4014 1.10547 10.3152C1.10547 5.22908 5.2286 1.10596 10.3147 1.10596C15.4009 1.10596 19.524 5.22908 19.524 10.3152C19.524 12.5882 18.7006 14.6688 17.3357 16.2751L22.6756 21.615C22.9685 21.9079 22.9685 22.3827 22.6756 22.6756C22.3827 22.9685 21.9079 22.9685 21.615 22.6756L16.2751 17.3358ZM10.4395 5.78662C10.8537 5.78662 11.1895 6.12241 11.1895 6.53662V9.68896H14.3413C14.7556 9.68896 15.0913 10.0248 15.0913 10.439C15.0913 10.8532 14.7556 11.189 14.3413 11.189H11.1895V14.3404C11.1895 14.7546 10.8537 15.0904 10.4395 15.0904C10.0252 15.0904 9.68945 14.7546 9.68945 14.3404V11.189H6.5376C6.12338 11.189 5.7876 10.8532 5.7876 10.439C5.7876 10.0248 6.12338 9.68896 6.5376 9.68896H9.68945V6.53662C9.68945 6.12241 10.0252 5.78662 10.4395 5.78662Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-zoom-in') === undefined) {
  customElements.define('cat-icon-zoom-in', BlocksIconZoomIn);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-zoom-in': BlocksIconZoomIn;
  }
}
